import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
} from '@mlm/ui';
import {
  getPosOrder,
  updateOrderMemo,
  updateOrderReason,
  voidOrder,
  type PosOrderDetail,
} from '../lib/api';
import { Receipt } from './receipt';
import { orderToReceipt } from '../lib/order-receipt';
import { hasPerm, useHasPerm } from '../lib/auth-store';
import { posStatusVariant } from '../lib/pos-status';
import { salesOrderSeries } from '../lib/sales-order';

/** Friendly copy for the void guardrail errors a cashier can hit. */
function prettyVoidError(code: string): string {
  switch (code) {
    case 'kit_code_already_used':
      return 'This sale activated a slot (a kit code was redeemed) — it can no longer be voided.';
    case 'order_not_refundable':
      return 'This order is no longer in a voidable state.';
    case 'cd_debt_settled':
      return 'This sale paid off a CD debt that has since settled — an admin must handle it.';
    default:
      return code.replace(/_/g, ' ');
  }
}

/** Peso-format a decimal-string money value. */
function peso(v: string): string {
  return `₱${Number(v).toFixed(2)}`;
}

/**
 * Read-only POS transaction detail (Transaction History → click a row).
 * Shows items, totals, payments, and any kit codes. Actions (void/refund)
 * stay on their dedicated pages — this is a view.
 */
export function TransactionDetailDialog({
  orderId,
  orderPrefix,
  onClose,
}: {
  orderId: string | null;
  /** Active branch's code-prefix, stripped to show the bare "#0001" series. */
  orderPrefix?: string | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ['pos-order', orderId],
    queryFn: () => getPosOrder(orderId!),
    enabled: !!orderId,
  });
  const order = query.data;

  const [voiding, setVoiding] = useState(false);
  const [reason, setReason] = useState('');
  const [voidError, setVoidError] = useState<string | null>(null);
  const [reprinting, setReprinting] = useState(false);

  const voidSale = useMutation({
    mutationFn: (r: string) => voidOrder(orderId!, r),
    meta: { successMessage: 'Order voided.' },
    onSuccess: () => {
      setVoiding(false);
      setReason('');
      setVoidError(null);
      // Refetch this order (status → refunded, codes voided) and the history list.
      qc.invalidateQueries({ queryKey: ['pos-order', orderId] });
      qc.invalidateQueries({ queryKey: ['pos-transactions'] });
    },
    onError: (err: unknown) => {
      const code = (err as { response?: { data?: { error?: string } } }).response?.data?.error;
      setVoidError(prettyVoidError(code ?? 'void_failed'));
    },
  });

  // A completed sale is voidable only when nothing has been redeemed (server-
  // computed `voidable`) and the cashier holds the refund permission.
  const canVoid = !!order && order.voidable === true && hasPerm('pos.refund');

  // Amending a remark after the fact is supervisor authority (manager/admin by
  // default), separate from taking the sale. Reactive hook, not the one-shot
  // `hasPerm`: this drives rendered affordances, which must follow a session
  // restore or swap.
  const canEditMemo = useHasPerm('pos.memo.edit');
  const [editingMemo, setEditingMemo] = useState(false);
  const [memoDraft, setMemoDraft] = useState('');
  const saveMemo = useMutation({
    mutationFn: (notes: string) => updateOrderMemo(orderId!, notes.trim() ? notes.trim() : null),
    meta: { successMessage: 'Remarks saved.' },
    onSuccess: () => {
      setEditingMemo(false);
      qc.invalidateQueries({ queryKey: ['pos-order', orderId] });
      // The list shows a "has remarks" glyph, so it has to refetch too.
      qc.invalidateQueries({ queryKey: ['pos-transactions'] });
    },
  });

  // The tape replaces the whole view while printing — `.receipt-tape` is the
  // only element that survives `@media print`, so it must not sit inside the
  // dialog's scroll container.
  if (reprinting && order) {
    return <ReprintReceipt order={order} onClose={() => setReprinting(false)} />;
  }

  return (
    <Dialog open={!!orderId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span>
              Sales Order
              {order?.code ? (
                <span className="font-mono"> #{salesOrderSeries(order.code, orderPrefix)}</span>
              ) : ''}
            </span>
            {order && <Badge variant={posStatusVariant(order.status)}>{order.status}</Badge>}
          </DialogTitle>
        </DialogHeader>

        {query.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : !order ? (
          <p className="text-sm text-muted-foreground">Couldn't load this sales order.</p>
        ) : (
          <div className="space-y-4 text-sm">
            {/* Meta */}
            <div className="grid grid-cols-2 gap-y-1 text-xs text-muted-foreground">
              <span>Created</span>
              <span className="text-right text-foreground">
                {new Date(order.createdAt).toLocaleString()}
              </span>
              <span>Buyer</span>
              <span className="text-right text-foreground">{order.buyerName ?? '—'}</span>
              <span>Email</span>
              <span className="text-right text-foreground break-all">
                {order.buyerEmail ?? '—'}
              </span>
              {order.releasedAt && (
                <>
                  <span>Released</span>
                  <span className="text-right text-foreground">
                    {new Date(order.releasedAt).toLocaleString()}
                  </span>
                </>
              )}
              {order.branch && (
                <>
                  <span>Branch</span>
                  <span className="text-right text-foreground">
                    {order.branch.name} ({order.branch.code})
                  </span>
                </>
              )}
            </div>

            {/* Items */}
            <div>
              <div className="mb-1 text-xs font-medium text-muted-foreground">Items</div>
              {order.items.length === 0 ? (
                <p className="text-xs text-muted-foreground">No line items.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="text-left text-xs text-muted-foreground border-b">
                    <tr>
                      <th className="py-1">Product</th>
                      <th className="py-1 text-right">Qty</th>
                      <th className="py-1 text-right">Unit</th>
                      <th className="py-1 text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {order.items.map((it, i) => (
                      <tr key={i} className="border-b last:border-0">
                        <td className="py-1 pr-2">{it.variantName ? `${it.name} — ${it.variantName}` : it.name}</td>
                        <td className="py-1 text-right tabular-nums">{it.qty}</td>
                        <td className="py-1 text-right tabular-nums">{peso(it.unitPrice)}</td>
                        <td className="py-1 text-right tabular-nums">{peso(it.lineTotal)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Totals */}
            <div className="space-y-1 border-t pt-2">
              <Row label="Subtotal" value={peso(order.subtotal)} />
              {Number(order.discountTotal) > 0 && (
                <Row label="Discount" value={`-${peso(order.discountTotal)}`} />
              )}
              <div className="flex justify-between font-semibold">
                <span>Total</span>
                <span className="tabular-nums">{peso(order.total)}</span>
              </div>
            </div>

            {/* Payments */}
            {order.payments.length > 0 && (
              <div className="border-t pt-2">
                <div className="mb-1 text-xs font-medium text-muted-foreground">Payment</div>
                {order.payments.map((p) => (
                  <div key={p.id} className="flex justify-between text-xs">
                    <span className="capitalize text-muted-foreground">
                      {p.method} · {p.status}
                    </span>
                    <span className="tabular-nums">
                      {peso(p.amount)}
                      {Number(p.changeDue) > 0 && (
                        <span className="text-muted-foreground"> (change {peso(p.changeDue)})</span>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Remarks — typed at checkout, amendable afterwards by a
                `pos.memo.edit` holder. Rendered even when empty for a holder,
                so "add a remark to this sale" is reachable at all. */}
            {(order.notes || canEditMemo) && (
              <div className="border-t pt-2">
                <div className="mb-1 flex items-center gap-2">
                  <span className="text-xs font-medium text-muted-foreground">Remarks</span>
                  {canEditMemo && !editingMemo && (
                    <button
                      type="button"
                      className="text-[11px] text-muted-foreground underline hover:text-foreground"
                      onClick={() => {
                        setMemoDraft(order.notes ?? '');
                        setEditingMemo(true);
                      }}
                    >
                      {order.notes ? 'Edit' : 'Add'}
                    </button>
                  )}
                </div>
                {editingMemo ? (
                  <div className="space-y-2">
                    <Input
                      value={memoDraft}
                      onChange={(e) => setMemoDraft(e.target.value)}
                      placeholder="Remarks (leave blank to clear)"
                      className="h-8"
                      maxLength={1000}
                    />
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        disabled={saveMemo.isPending}
                        onClick={() => saveMemo.mutate(memoDraft)}
                      >
                        {saveMemo.isPending ? 'Saving…' : 'Save'}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={saveMemo.isPending}
                        onClick={() => setEditingMemo(false)}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <p className="whitespace-pre-wrap text-sm">
                    {order.notes ?? <span className="text-muted-foreground">—</span>}
                  </p>
                )}
                {/* The audit trail is admin-only, so the till carries its own
                    "this was amended" marker. */}
                {order.notesUpdatedAt && !editingMemo && (
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    Edited{order.notesUpdatedByName ? ` by ${order.notesUpdatedByName}` : ''} ·{' '}
                    {new Date(order.notesUpdatedAt).toLocaleString()}
                  </p>
                )}
              </div>
            )}

            {/* Void / refund reason. Captured at the void but displayed nowhere
                until now — it only ever reached the audit log. */}
            {order.reasonHistory?.length > 0 && (
              <div className="border-t pt-2">
                <div className="mb-1 text-xs font-medium text-muted-foreground">
                  Void / refund reason
                </div>
                {order.reasonHistory.map((h) => (
                  <ReasonRow
                    key={h.id}
                    orderId={order.id}
                    entry={h}
                    canEdit={canEditMemo}
                    onSaved={() => qc.invalidateQueries({ queryKey: ['pos-order', orderId] })}
                  />
                ))}
              </div>
            )}

            {/* Kit codes */}
            {order.kitCodes.length > 0 && (
              <div className="border-t pt-2">
                <div className="mb-1 text-xs font-medium text-muted-foreground">Kit codes</div>
                {order.kitCodes.map((c) => (
                  <div key={c.id} className="flex justify-between text-xs">
                    <span className="font-mono">
                      {c.code} · PIN {c.pin}
                    </span>
                    <span className={c.redeemedAt ? 'text-muted-foreground' : 'text-emerald-600'}>
                      {c.redeemedAt ? 'redeemed' : 'unused'}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Reprint — available for ANY sale with a tape to print, including
                a voided/refunded one (a cashier often needs the paper trail for
                exactly those). Deliberately not permission-gated: reprinting
                reads nothing the cashier can't already see on this screen. */}
            <div className="border-t pt-3">
              <Button size="sm" variant="outline" onClick={() => setReprinting(true)}>
                Reprint receipt
              </Button>
            </div>

            {/* Void action — only for a completed, still-voidable sale. Voiding
                reverses the sale, restocks, and invalidates any unused kit codes. */}
            {canVoid && (
              <div className="border-t pt-3">
                {voiding ? (
                  <div className="space-y-2">
                    <Input
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder="Reason (required)"
                      className="h-8"
                    />
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="destructive"
                        disabled={!reason.trim() || voidSale.isPending}
                        onClick={() => voidSale.mutate(reason.trim())}
                      >
                        {voidSale.isPending ? 'Voiding…' : 'Confirm void'}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={voidSale.isPending}
                        onClick={() => {
                          setVoiding(false);
                          setReason('');
                          setVoidError(null);
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                    <p className="text-[11px] text-muted-foreground">
                      Voiding reverses this sale, restocks the items, and makes any unused
                      Code+PIN from it disappear.
                    </p>
                  </div>
                ) : (
                  <Button size="sm" variant="outline" onClick={() => setVoiding(true)}>
                    Void sale
                  </Button>
                )}
                {voidError && <p className="mt-2 text-xs text-destructive">{voidError}</p>}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * One void/refund reason, optionally editable.
 *
 * The reason is required at the void, but until now it was written straight to
 * the audit log and a status-history row that nothing rendered — so a wrong or
 * hasty reason was invisible AND unfixable.
 */
function ReasonRow({
  orderId,
  entry,
  canEdit,
  onSaved,
}: {
  orderId: string;
  entry: PosOrderDetail['reasonHistory'][number];
  canEdit: boolean;
  onSaved: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(entry.note ?? '');
  const save = useMutation({
    mutationFn: (note: string) => updateOrderReason(orderId, entry.id, note.trim()),
    meta: { successMessage: 'Reason saved.' },
    onSuccess: () => {
      setEditing(false);
      onSaved();
    },
  });

  return (
    <div className="mb-1.5 last:mb-0">
      {editing ? (
        <div className="space-y-2">
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Reason (required)"
            className="h-8"
            maxLength={500}
          />
          <div className="flex gap-2">
            <Button
              size="sm"
              disabled={!draft.trim() || save.isPending}
              onClick={() => save.mutate(draft)}
            >
              {save.isPending ? 'Saving…' : 'Save'}
            </Button>
            <Button size="sm" variant="outline" disabled={save.isPending} onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="whitespace-pre-wrap text-sm">{entry.note ?? '—'}</p>
            <p className="text-[11px] text-muted-foreground">
              {entry.status}
              {entry.changedByName ? ` · ${entry.changedByName}` : ''} ·{' '}
              {new Date(entry.changedAt).toLocaleString()}
            </p>
          </div>
          {canEdit && (
            <button
              type="button"
              className="flex-none text-[11px] text-muted-foreground underline hover:text-foreground"
              onClick={() => {
                setDraft(entry.note ?? '');
                setEditing(true);
              }}
            >
              Edit
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Reprint a past sale's tape from Transaction History.
 *
 * Prints the SAME tape the sale originally produced — `orderToReceipt` is the
 * single mapper shared with the releasing queue, so a reprint cannot disagree
 * with the customer's original copy.
 *
 * A reprint is deliberately marked on the tape. An unmarked duplicate of a
 * cash receipt is a reconciliation hazard: two identical tapes for one sale is
 * how a sale gets counted twice at shift close.
 */
export function ReprintReceipt({ order, onClose }: { order: PosOrderDetail; onClose: () => void }) {
  // Rendered through a PORTAL to <body>, not inline.
  //
  // The print stylesheet hides the rest of the app with `visibility: hidden`,
  // which conceals elements but leaves them occupying their full height. The
  // Transaction History table can be 200 rows tall, so printing a reprint
  // emitted ~8 pages: the tape on page 1 and seven blank ones padded out by the
  // invisible table. (The releasing queue never hit this because it REPLACES
  // its page content, unmounting the table.)
  //
  // As a direct child of <body> this can be isolated with `display: none` on
  // its siblings — which does collapse them out of the layout. See the
  // `.print-root` rules in index.css.
  // Tag <body> while the tape is mounted so the print stylesheet can collapse
  // everything else. Cleaned up on unmount — a stale class would blank the app
  // on the NEXT print from any other page.
  useEffect(() => {
    document.body.classList.add('printing-receipt');
    return () => document.body.classList.remove('printing-receipt');
  }, []);

  return createPortal(
    <div className="print-root fixed inset-0 z-50 overflow-y-auto bg-background p-4">
      <p className="no-print mx-auto mb-2 max-w-md text-center text-xs text-muted-foreground">
        Reprint of sale {order.code}
      </p>
      <Receipt data={orderToReceipt(order)} onClose={onClose} />
    </div>,
    document.body,
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between text-muted-foreground">
      <span>{label}</span>
      <span className="tabular-nums text-foreground">{value}</span>
    </div>
  );
}
